import { useCallback, useEffect, useRef, useState } from 'react';

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const pickMime = (types) => {
  if (typeof window.MediaRecorder === 'undefined') return '';
  return types.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || '';
};

export const deviceInfo = () => {
  const ua = navigator.userAgent || '';
  let browser = 'Other';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  return { browser, device: /Mobi|Android|iPhone|iPad/i.test(ua) ? 'mobile' : 'desktop' };
};

// RMS level of the microphone track (0..~0.5). One AudioContext per meter; close() when done.
export function createLevelMeter(stream) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  ctx.resume?.().catch(() => {});
  const src = ctx.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
  const an = ctx.createAnalyser();
  an.fftSize = 1024;
  src.connect(an);
  const buf = new Float32Array(an.fftSize);
  return {
    read() {
      an.getFloatTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i += 1) s += buf[i] * buf[i];
      return Math.sqrt(s / buf.length);
    },
    resume: () => ctx.resume?.().catch(() => {}),
    close() {
      try { src.disconnect(); ctx.close(); } catch (e) { /* already closed */ }
    },
  };
}

export function useMediaStream() {
  const ref = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    ref.current?.getTracks().forEach((t) => t.stop());
    ref.current = null;
    setStream(null);
  }, []);

  const ensure = useCallback(async (deviceId) => {
    const cur = ref.current;
    const live = cur && cur.getTracks().length > 0 && cur.getTracks().every((t) => t.readyState === 'live');
    const same = !deviceId || cur?.getVideoTracks()[0]?.getSettings().deviceId === deviceId;
    if (live && same) return cur;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('unsupported');
      throw new Error('unsupported');
    }
    cur?.getTracks().forEach((t) => t.stop());
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 },
          ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' }) },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      ref.current = s;
      setStream(s);
      setError(null);
      return s;
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'denied' : e?.name === 'NotFoundError' ? 'missing' : 'failed');
      throw e;
    }
  }, []);

  useEffect(() => () => ref.current?.getTracks().forEach((t) => t.stop()), []);
  return { stream, error, ensure, stop, ref };
}

// Callback ref that binds a MediaStream to a <video>, even when the element mounts later.
export function useStreamVideo(stream) {
  const ref = useRef(null);
  const setRef = useCallback((el) => {
    ref.current = el;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.play?.().catch(() => {});
    }
  }, [stream]);
  return [ref, setRef];
}

export const MEDIA_ERRORS = {
  denied: 'Camera or microphone access was blocked. Allow access in your browser\'s site settings (the camera icon in the address bar), then try again.',
  missing: 'We could not find a camera or microphone. Connect one and try again.',
  unsupported: 'This browser cannot access a camera. Please use a recent version of Chrome, Edge, Firefox or Safari.',
  failed: 'Your camera or microphone could not be started. Close other apps that may be using it, then try again.',
};
